import React, { useCallback, useEffect, useState } from "react";
import baseUrl from "../../../../api/baseUrl";
import InventoryIcon from "@mui/icons-material/Inventory";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";

const tableHeader = ["No", "Name", "Total", "Discount", "Net Total", "Card", "Cash", "Date"];

function AllRelease() {
  const [data, setdata] = useState([]);
  const [select, setselect] = useState("patient");

  const getReleaseData = useCallback(async (select: string) => {
    try {
      const response = await baseUrl.get(
        "client/inventory/release/findAllRelease",
        { headers: { type: select } }
      );
      setdata(response.data.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getReleaseData(select);
  }, []);

  const handleChange = (value: string) => {
    setselect(value);
    getReleaseData(value);
  };

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-primary/10 dark:bg-primary/30 rounded-md flex items-center justify-center">
              <InventoryIcon className="text-primary dark:text-sky-300" sx={{ fontSize: 14 }} />
            </div>
            <div>
              <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">All Releases</h2>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">{data.length} records found</p>
            </div>
          </div>
          <div className="flex gap-1.5 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
            <button
              onClick={() => handleChange("patient")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                select === "patient"
                  ? "bg-white dark:bg-slate-600 text-primary dark:text-sky-300 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <PersonIcon sx={{ fontSize: 14 }} />
              Patient
            </button>
            <button
              onClick={() => handleChange("employee")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                select === "employee"
                  ? "bg-white dark:bg-slate-600 text-primary dark:text-sky-300 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <BadgeIcon sx={{ fontSize: 14 }} />
              Employee
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {data?.map((item: any, i: number) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">#{i + 1}</span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400">{item?.createdAt?.split("T")[0]}</span>
            </div>
            <h3 className="font-semibold text-xs text-slate-800 dark:text-slate-100 mb-2">
              {select === "patient" ? item?.patientId?.fullName : item?.employeeId?.firstName}
            </h3>
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              <div className="bg-slate-50 dark:bg-slate-700 rounded-md p-1.5">
                <p className="text-slate-500 dark:text-slate-400 text-[10px]">Total</p>
                <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">{item?.total}</p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700 rounded-md p-1.5">
                <p className="text-slate-500 dark:text-slate-400 text-[10px]">Discount</p>
                <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">{item?.discount}</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/40 rounded-md p-1.5">
                <p className="text-emerald-600 dark:text-emerald-300 text-[10px]">Net Total</p>
                <p className="font-semibold text-xs text-emerald-700 dark:text-emerald-300">{item?.netTotal}</p>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/40 rounded-md p-1.5">
                <p className="text-indigo-600 dark:text-indigo-300 text-[10px]">Card / Cash</p>
                <p className="font-semibold text-xs text-indigo-700 dark:text-indigo-300">{item?.card} / {item?.cash}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-slate-200 dark:border-slate-600">
            <tr>
              {tableHeader.map((title, i) => (
                <th key={i} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
            {data?.map((item: any, i: number) => (
              <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{i + 1}</td>
                <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-100">
                  {select === "patient" ? item?.patientId?.fullName : item?.employeeId?.firstName}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.total}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.discount}</td>
                <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-300">{item?.netTotal}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.card}</td>
                <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.cash}</td>
                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{item?.createdAt?.split("T")[0]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AllRelease;
